import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity, sanitizePayload } from '../../utils/security';
import { toast } from 'react-toastify';
import { RefreshCw, Trash2, ExternalLink, Globe, Phone, Mail, X, Clock, User, FileText, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminAuditList = () => {
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const { t } = useTranslation();
    const [audits, setAudits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAudit, setSelectedAudit] = useState(null);

    const fetchAudits = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}audits.php`);
            const data = await res.json();
            if (Array.isArray(data)) setAudits(data);
        } catch {
            toast.error(t('adminAudit.toastLoadError') || 'აუდიტების ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, [t]);

    useEffect(() => {
        if (user && isSuperAdmin()) fetchAudits();
    }, [user, isSuperAdmin, fetchAudits]);

    const handleStatusChange = async (id, newStatus) => {
        try {
            const sanitized = sanitizePayload({ status: newStatus });
            const res = await fetchWithSecurity(`${API_URL}audits.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, ...sanitized })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(t('adminAudit.toastStatusUpdated') || 'სტატუსი განახლდა');
                setAudits(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));
            } else {
                toast.error(data.error || (t('adminAudit.toastStatusError') || 'სტატუსის განახლება ვერ მოხერხდა'));
            }
        } catch {
            toast.error(t('adminAudit.toastStatusError') || 'სტატუსის განახლება ვერ მოხერხდა');
        }
    };

    const deleteAudit = async (id) => {
        if (!window.confirm(t('adminAudit.deleteConfirm') || 'დარწმუნებული ხართ, რომ გსურთ ამ აუდიტის განაცხადის წაშლა?')) return;
        try {
            const res = await fetchWithSecurity(`${API_URL}audits.php?id=${id}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                toast.success(t('adminAudit.toastDeleted') || 'განაცხადი წაიშალა');
                setAudits(prev => prev.filter(a => a.id !== id));
            } else {
                toast.error(data.error || (t('adminAudit.toastDeleteError') || 'წაშლა ვერ მოხერხდა'));
            }
        } catch {
            toast.error(t('adminAudit.toastDeleteError') || 'წაშლა ვერ მოხერხდა');
        }
    };

    if (authLoading || !user) return null;

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'New': return 'shadcn-badge-danger';
            case 'In Progress': return 'shadcn-badge-warning';
            case 'Completed': return 'shadcn-badge-success';
            case 'Archived': return 'shadcn-badge-secondary';
            default: return 'shadcn-badge-secondary';
        }
    };

    return (
        <AdminLayout title={t('adminAudit.title') || 'ვებგვერდის აუდიტის განაცხადები'}>
            <div className="shadcn-card">
                <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                    <div className="shadcn-card-title">
                        <Globe size={20} className="text-indigo" />
                        <span>{t('adminAudit.title') || 'ვებგვერდის აუდიტის განაცხადები'} ({audits.length})</span>
                    </div>
                    <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={fetchAudits}>
                        <RefreshCw size={14} /> {t('adminAudit.refresh') || 'განახლება'}
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-5 text-muted">
                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                        <span>{t('adminAudit.loading') || 'აუდიტის განაცხადები იტვირთება...'}</span>
                    </div>
                ) : audits.length === 0 ? (
                    <p className="text-center text-muted py-5 mb-0">{t('adminAudit.noAudits') || 'აუდიტის განაცხადები ჯერ არ არის.'}</p>
                ) : (
                    <div className="shadcn-table-wrapper">
                        <table className="shadcn-table">
                            <thead>
                                <tr>
                                    <th>{t('adminAudit.colDate') || 'თარიღი & დრო'}</th>
                                    <th>{t('adminAudit.colClient') || 'კლიენტის სახელი'}</th>
                                    <th>{t('adminAudit.colContact') || 'საკონტაქტო ინფო'}</th>
                                    <th>{t('adminAudit.colWebsite') || 'ვებგვერდი (URL)'}</th>
                                    <th>{t('adminAudit.colStatus') || 'სტატუსი'}</th>
                                    <th>{t('adminAudit.colActions') || 'მოქმედებები'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {audits.map(audit => (
                                    <tr key={audit.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedAudit(audit)}>
                                        <td>
                                            <div className="fw-semibold text-dark">{new Date(audit.created_at).toLocaleDateString()}</div>
                                            <div className="smallest text-muted">{new Date(audit.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                        </td>
                                        <td className="fw-bold text-dark">{audit.name}</td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1 text-dark small mb-1">
                                                <Mail size={13} className="text-primary" />
                                                <a href={`mailto:${audit.email}`} className="text-dark text-decoration-none">{audit.email}</a>
                                            </div>
                                            {audit.phone && (
                                                <div className="d-flex align-items-center gap-1 text-muted smallest">
                                                    <Phone size={11} />
                                                    <span>{audit.phone}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {audit.website_url ? (
                                                <a href={audit.website_url.startsWith('http') ? audit.website_url : `https://${audit.website_url}`} target="_blank" rel="noreferrer" className="d-inline-flex align-items-center gap-1 text-primary fw-semibold small text-decoration-none">
                                                    <Globe size={14} />
                                                    <span>{audit.website_url.replace(/^https?:\/\//, '')}</span>
                                                </a>
                                            ) : (
                                                <span className="text-muted smallest">არ არის მითითებული</span>
                                            )}
                                        </td>
                                        <td>
                                            <select
                                                className={`shadcn-select form-select-sm py-1 ${getStatusBadgeClass(audit.status)}`}
                                                value={audit.status}
                                                onChange={(e) => handleStatusChange(audit.id, e.target.value)}
                                                style={{ maxWidth: '140px' }}
                                            >
                                                <option value="New">{t('adminAudit.statusNew') || 'ახალი'}</option>
                                                <option value="In Progress">{t('adminAudit.statusInProgress') || 'მიმდინარე'}</option>
                                                <option value="Completed">{t('adminAudit.statusCompleted') || 'დასრულებული'}</option>
                                                <option value="Archived">{t('adminAudit.statusArchived') || 'დაარქივებული'}</option>
                                            </select>
                                        </td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <div className="d-flex gap-2">
                                                <Link to={`/admin/audits/detail/${audit.id}`} className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary">
                                                    <ExternalLink size={14} /> {t('common.view') || 'ნახვა'}
                                                </Link>
                                                <button className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" onClick={() => deleteAudit(audit.id)}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Audit Detail Modal */}
            {selectedAudit && (
                <div
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
                    onClick={() => setSelectedAudit(null)}
                >
                    <div
                        style={{ background: '#fff', borderRadius: '16px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="d-flex align-items-center justify-content-between p-4 border-bottom" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', borderRadius: '16px 16px 0 0' }}>
                            <div className="d-flex align-items-center gap-3">
                                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', padding: '8px' }}>
                                    <Globe size={20} style={{ color: '#fff' }} />
                                </div>
                                <div>
                                    <h5 className="fw-bold mb-0" style={{ color: '#fff' }}>{selectedAudit.name}</h5>
                                    <div className="small" style={{ color: 'rgba(255,255,255,0.8)' }}>{t('adminAudit.title') || 'ვებგვერდის აუდიტის განაცხადი'} #{selectedAudit.id}</div>
                                </div>
                            </div>
                            <button onClick={() => setSelectedAudit(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', padding: '6px', color: '#fff', cursor: 'pointer' }}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-4">
                            <div className="row g-3">
                                {/* Date */}
                                <div className="col-12">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <Clock size={14} className="text-muted" />
                                        <span className="text-muted small fw-semibold">{t('adminAudit.colDate') || 'თარიღი'}</span>
                                    </div>
                                    <div className="fw-semibold text-dark">{new Date(selectedAudit.created_at).toLocaleString()}</div>
                                </div>

                                {/* Contact Info */}
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <User size={14} className="text-muted" />
                                        <span className="text-muted small fw-semibold">{t('adminAudit.colClient') || 'კლიენტი'}</span>
                                    </div>
                                    <div className="fw-bold text-dark">{selectedAudit.name}</div>
                                </div>

                                <div className="col-md-6">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <Mail size={14} className="text-muted" />
                                        <span className="text-muted small fw-semibold">{t('adminAudit.colContact') || 'ელფოსტი'}</span>
                                    </div>
                                    <a href={`mailto:${selectedAudit.email}`} className="fw-semibold text-primary text-decoration-none">{selectedAudit.email}</a>
                                </div>

                                {selectedAudit.phone && (
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <Phone size={14} className="text-muted" />
                                            <span className="text-muted small fw-semibold">ტელეფონი</span>
                                        </div>
                                        <div className="fw-semibold text-dark">{selectedAudit.phone}</div>
                                    </div>
                                )}

                                {/* Website */}
                                {selectedAudit.website_url && (
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <Globe size={14} className="text-muted" />
                                            <span className="text-muted small fw-semibold">{t('adminAudit.colWebsite') || 'ვებგვერდი'}</span>
                                        </div>
                                        <a
                                            href={selectedAudit.website_url.startsWith('http') ? selectedAudit.website_url : `https://${selectedAudit.website_url}`}
                                            target="_blank" rel="noreferrer"
                                            className="d-inline-flex align-items-center gap-1 fw-semibold text-primary text-decoration-none"
                                        >
                                            <Globe size={14} />
                                            {selectedAudit.website_url.replace(/^https?:\/\//, '')}
                                            <ExternalLink size={12} />
                                        </a>
                                    </div>
                                )}

                                {/* Status */}
                                <div className="col-12">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <CheckCircle size={14} className="text-muted" />
                                        <span className="text-muted small fw-semibold">{t('adminAudit.colStatus') || 'სტატუსი'}</span>
                                    </div>
                                    <select
                                        className={`shadcn-select form-select-sm py-1 ${getStatusBadgeClass(selectedAudit.status)}`}
                                        value={selectedAudit.status}
                                        onChange={(e) => {
                                            handleStatusChange(selectedAudit.id, e.target.value);
                                            setSelectedAudit(prev => ({ ...prev, status: e.target.value }));
                                        }}
                                        style={{ maxWidth: '200px' }}
                                    >
                                        <option value="New">{t('adminAudit.statusNew') || 'ახალი'}</option>
                                        <option value="In Progress">{t('adminAudit.statusInProgress') || 'მიმდინარე'}</option>
                                        <option value="Completed">{t('adminAudit.statusCompleted') || 'დასრულებული'}</option>
                                        <option value="Archived">{t('adminAudit.statusArchived') || 'დაარქივებული'}</option>
                                    </select>
                                </div>

                                {/* Notes / Message */}
                                {selectedAudit.message && (
                                    <div className="col-12">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <FileText size={14} className="text-muted" />
                                            <span className="text-muted small fw-semibold">შეტყობინება / ჩანაწერები</span>
                                        </div>
                                        <div className="p-3 rounded-3" style={{ background: '#f8fafc', border: '1px solid var(--admin-border)', whiteSpace: 'pre-wrap', fontSize: '0.875rem', color: '#1e293b' }}>
                                            {selectedAudit.message}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="d-flex gap-2 mt-4 pt-3 border-top">
                                <Link to={`/admin/audits/detail/${selectedAudit.id}`} className="shadcn-btn shadcn-btn-primary shadcn-btn-sm">
                                    <ExternalLink size={14} /> {t('common.view') || 'დეტალური ნახვა'}
                                </Link>
                                <button className="shadcn-btn shadcn-btn-danger shadcn-btn-sm" onClick={() => { deleteAudit(selectedAudit.id); setSelectedAudit(null); }}>
                                    <Trash2 size={14} /> {t('common.delete') || 'წაშლა'}
                                </button>
                                <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm ms-auto" onClick={() => setSelectedAudit(null)}>
                                    <X size={14} /> {t('common.close') || 'დახურვა'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminAuditList;
